import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type PackagingWarehouseInstructionsFormValues, packagingWarehouseInstructionsFormSchema } from "../schemas"

import { PackagingWarehouseInstructionsForm } from "./PackagingWarehouseInstructionsForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"

interface PackagingWarehouseInstructionsFormDialogProps {
  initialValues: PackagingWarehouseInstructionsFormValues
  isUpdating: boolean
  onClose: () => void
  onSubmit: (values: PackagingWarehouseInstructionsFormValues) => Promise<void>
}

export const PackagingWarehouseInstructionsFormDialog: FC<PackagingWarehouseInstructionsFormDialogProps> = ({
  initialValues,
  isUpdating,
  onClose,
  onSubmit,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)

  const methods = useForm<PackagingWarehouseInstructionsFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(packagingWarehouseInstructionsFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods

  const canSubmit = isDirty && isValid && !isSubmitting && !isUpdating

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleConfirmClose = () => {
    setIsCancelDialogOpen(false)
    onClose()
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />
  ) : (
    <Dialog open fullWidth onClose={handleClose} data-testid='customer-product.packaging-warehouse-form-dialog'>
      <DialogHeader title='Edit Packaging & Warehouse Instructions' onClose={handleClose} />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <PackagingWarehouseInstructionsForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleClose}
          data-testid='customer-product.packaging-warehouse-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmit)}
          data-testid='customer-product.packaging-warehouse-form-dialog.submit-button'
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
